import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { PRODUCT } from 'routes';

import { getProductList } from 'api';
import Block from 'components/Block';
import Container from 'components/Container';
import Grid from 'components/Grid';
import MetaTitle from 'components/MetaTitle';
import { LazyPagination } from 'components/Pagination';
import ProductCard from 'components/ProductCard';
import Typography from 'components/Typography';
import { DEFAULT_PAGE_SIZE } from 'consts';
import Layout from 'containers/Layout';
import Main from 'containers/Main';
import with404 from 'hocs/with404';
import { EnhancedNextPage, EnhancedPageContext } from 'types/Next';
import { Paginated } from 'types/Paginated';
import { Product } from 'types/Product';
import { parsePagination, setPageSize } from 'utils/paginate';

export type SearchResultsProps = {
  data?: Paginated<Product>;
  pageSize?: number;
  notFound: boolean;
};

const SearchResultsPage: EnhancedNextPage<SearchResultsProps> = ({
  data,
  pageSize = DEFAULT_PAGE_SIZE,
}) => {
  const router = useRouter();
  const search: string | undefined = router.query.search?.toString();

  if (!data) return null;

  const { results: products } = data;

  return (
    <>
      <MetaTitle>Products</MetaTitle>
      <Layout>
        <Main>
          <Container>
            <Block>
              {products.length > 0 ? (
                <Typography>{`Search result for "${search}"`}</Typography>
              ) : (
                <div>
                  <Typography
                    block
                  >{`Your search for "${search}" did not match any products.`}</Typography>
                  <Typography block color="faded">
                    Please check you spelling or try another search term.
                  </Typography>
                </div>
              )}
            </Block>

            {products.length > 0 && (
              <Grid columns={3} gap="regular">
                {products.map(product => (
                  <Link
                    href={{ pathname: PRODUCT.pathname, query: { id: product.slug } }}
                    passHref
                    key={product.id}
                    legacyBehavior
                  >
                    <ProductCard product={product} />
                  </Link>
                ))}
              </Grid>
            )}

            {products.length > 0 && (
              <LazyPagination
                data={data}
                pageSize={pageSize}
                setPageSize={s => setPageSize(router, s)}
              />
            )}
          </Container>
        </Main>
      </Layout>
    </>
  );
};

SearchResultsPage.getInitialProps = async ({ query, req }: EnhancedPageContext) => {
  let data: Paginated<Product> | undefined = undefined;
  const { page, pageSize } = parsePagination(query, req);
  const search: string | undefined = query.search?.toString();

  try {
    const response = await getProductList({ page, page_size: pageSize, search });

    data = response.data;
  } catch (e) {
    console.error('ERROR2', e);
  }

  return {
    props: {
      data,
      pageSize,
    },
    notFound: !data,
  };
};

export default with404<SearchResultsProps>(SearchResultsPage);
